/**
 * TermsScreen — Termini di Servizio consultabili in-app (route /termini).
 * Sempre raggiungibile dal menu e dalla spunta di accettazione in onboarding.
 * Standalone: non richiede login (apribile anche in nuova scheda durante il
 * primo claim). Il testo rispecchia il PDF NULLUM-termini-servizio.pdf.
 */

import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box } from '../components/Frame';
import { TOS_VERSION } from '../lib/legal';

function H({ children }) {
  return <p className="text-ink text-[15px] mt-4 mb-1 border-b border-ink-dim/40 pb-1">{children}</p>;
}

export default function TermsScreen() {
  const navigate = useNavigate();
  return (
    <Frame statusLeft="TERMINI" statusRight="5G">
      <Header title="// Termini di Servizio" crumb="◀ indietro" onCrumbClick={() => navigate(-1)} />

      <p className="text-[13px] text-ink-dim mb-2">
        Versione {TOS_VERSION}. Fornitore: <b className="text-ink">SKEMATIKA S.r.l.</b>,
        Via Pier Luigi Nervi 190, 04100 Latina (LT) — P.IVA 03198400594 — PEC skematika@pec.it.
      </p>
      <Box variant="warn" className="text-[12px] mb-3 text-ink-dim">
        Documento in bozza (in fase di validazione legale). Fa fede la versione pubblicata su nullum.app.
      </Box>

      <div className="text-[14px] text-ink-dim space-y-2">
        <H>1 · Chi siamo e oggetto</H>
        <p>Nullum ("il Servizio") è un'applicazione di messaggistica anonima, cifrata end-to-end ed effimera, fornita da SKEMATIKA S.r.l. ("il Fornitore"). Utilizzando il Servizio accetti i presenti Termini. Se non li accetti, non utilizzare il Servizio.</p>

        <H>2 · Requisiti</H>
        <ul className="list-disc pl-5 space-y-1">
          <li>Devi avere almeno <b className="text-ink">16 anni</b>.</li>
          <li>Devi avere la capacità giuridica di accettare un contratto vincolante.</li>
          <li>Ti impegni a usare il Servizio nel rispetto della legge applicabile.</li>
        </ul>

        <H>3 · Natura del Servizio e responsabilità sulla tua identità</H>
        <p>La tua identità è uno pseudonimo (@sigil) generato da te. Di conseguenza:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Sei l'<b className="text-ink">unico responsabile</b> della custodia delle tue 24 parole di recupero e del PIN.</li>
          <li>Il Fornitore <b className="text-ink">non può recuperare</b> un'identità persa né reimpostare PIN o parole: non li conosce.</li>
          <li>I messaggi sono <b className="text-ink">effimeri</b>: spariscono secondo la durata (TTL) scelta. Nessuna conservazione è garantita.</li>
        </ul>

        <H>4 · Usi vietati</H>
        <p>Ti impegni a NON utilizzare il Servizio per attività illecite o lesive, tra cui, a titolo non esaustivo:</p>
        <ul className="list-disc pl-5 space-y-1 text-amber">
          <li>materiale pedopornografico o di abuso su minori;</li>
          <li>terrorismo, violenza, traffico di armi, droga o esseri umani;</li>
          <li>minacce, molestie, stalking, incitamento all'odio o alla violenza;</li>
          <li>frodi, truffe, phishing, riciclaggio, estorsione;</li>
          <li>malware, accesso abusivo a sistemi, attacchi al Servizio o ad altri;</li>
          <li>violazione di diritti altrui (proprietà intellettuale, riservatezza, immagine);</li>
          <li>impersonare altri o creare identità ingannevoli a fini illeciti;</li>
          <li>spam, invii massivi non richiesti, abuso degli inviti.</li>
        </ul>
        <p>Sei l'<b className="text-ink">unico responsabile</b> dei contenuti che invii e dell'uso che fai del Servizio.</p>

        <H>5 · Cifratura e impossibilità di accesso ai contenuti</H>
        <p>I contenuti sono cifrati end-to-end: il Fornitore <b className="text-ink">non può leggerli, decifrarli o consegnarli in chiaro</b>, per scelta progettuale. È un elemento essenziale del Servizio, non un difetto. Non vi è moderazione preventiva dei contenuti, che non sono accessibili al Fornitore.</p>

        <H>6 · Segnalazioni, abusi e provvedimenti</H>
        <p>Pur non potendo accedere ai contenuti, il Fornitore prende sul serio gli abusi. Puoi segnalare violazioni a <b className="text-ink">info@nullum.app</b>. Nei limiti tecnici, il Fornitore può sospendere/revocare inviti, sigil o account che violano i Termini, limitare l'accesso a chi abusa, e collaborare con l'Autorità competente <b className="text-ink">nei limiti di ciò che possiede tecnicamente</b> (che non comprende contenuti in chiaro né identità reali).</p>

        <H>7 · Disponibilità del Servizio</H>
        <p>Il Servizio è fornito "così com'è" e "come disponibile", in fase beta. Non si garantiscono continuità, assenza di errori o conservazione dei dati; il Fornitore può modificare, sospendere o cessare il Servizio anche senza preavviso, salvo quanto inderogabile per legge.</p>

        <H>8 · Funzioni Premium e pagamenti</H>
        <p>Le funzioni Premium possono richiedere un abbonamento a pagamento gestito tramite Stripe. L'abbonamento ha una durata/scadenza indicate all'acquisto; al mancato rinnovo le funzioni si disattivano dopo un periodo di grazia e i relativi dati possono decadere. Come consumatore hai i diritti di legge (incluso, ove applicabile, il recesso), salve le eccezioni per i contenuti digitali fruiti subito col tuo consenso. I dati di pagamento sono trattati da Stripe; il Fornitore non conserva i dati della carta.</p>

        <H>9 · Proprietà intellettuale</H>
        <p>Il Servizio, il marchio "Nullum", il software e la grafica appartengono al Fornitore. I contenuti che invii restano tuoi: concedi solo i permessi tecnici minimi e temporanei per recapitarli (cifrati) fino alla scadenza.</p>

        <H>10 · Limitazione di responsabilità</H>
        <p>Nei limiti massimi consentiti dalla legge, il Fornitore non è responsabile dei contenuti degli utenti, della perdita di dati conseguente alla natura effimera del Servizio o alla perdita delle credenziali, né dei danni indiretti. Restano impregiudicati i diritti inderogabili del consumatore.</p>

        <H>11 · Modifiche ai Termini</H>
        <p>Il Fornitore può aggiornare i Termini. Le modifiche rilevanti saranno rese disponibili nell'app o sul sito. L'uso continuato dopo la pubblicazione vale come accettazione.</p>

        <H>12 · Legge applicabile e foro</H>
        <p>Termini regolati dalla legge italiana. Per le controversie è competente il foro del consumatore ove previsto da norma inderogabile; diversamente, il foro di Latina. Restano salve le tutele inderogabili del consumatore.</p>

        <H>13 · Contatti</H>
        <p>Per qualsiasi richiesta: <b className="text-ink">info@nullum.app</b> — PEC skematika@pec.it.</p>
      </div>

      <Footer>
        <Btn variant="ghost" onClick={() => navigate('/info')}>📖 Privacy & GDPR</Btn>
        <Btn variant="ghost" onClick={() => navigate(-1)}>◀ Indietro</Btn>
      </Footer>
    </Frame>
  );
}
